import { useQuery } from '@tanstack/react-query';
import { sessionResponseSchema, type SessionResponse } from '@bekebi/contracts/api';
import { apiRequest, isUnauthenticated, type ApiError } from '../api/client';

/**
 * The current session, read only from `GET /api/auth/session` — the single source of truth.
 * There is no locally stored or hard-coded user. Permissions in the session are used for
 * navigation only; the API enforces every permission itself.
 *
 * The session is NOT polled on a timer: every request to the API counts as activity on the
 * server and would keep an idle session alive. It is re-read on sign-in, on any 401, and when
 * real user activity occurs after a quiet period (see idle.ts).
 */
export const SESSION_QUERY_KEY = ['session'] as const;

export type SessionUser = SessionResponse['user'];

export type SessionState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: SessionUser; expiresAt: string }
  | { status: 'unauthenticated' }
  | { status: 'error'; error: ApiError | Error; retry: () => void };

/** Resolves to the session, or null when there is none (401 is a normal state, not an error). */
export async function fetchSession(): Promise<SessionResponse | null> {
  try {
    return await apiRequest('/auth/session', { schema: sessionResponseSchema });
  } catch (error) {
    if (isUnauthenticated(error)) return null;
    throw error;
  }
}

export function useSession(): SessionState {
  const query = useQuery({ queryKey: SESSION_QUERY_KEY, queryFn: fetchSession });

  if (query.isPending) return { status: 'loading' };
  // A failed (re-)check is an error, never a silently kept earlier session.
  if (query.isError) {
    return { status: 'error', error: query.error, retry: () => void query.refetch() };
  }
  if (query.data === null) return { status: 'unauthenticated' };
  return {
    status: 'authenticated',
    user: query.data.user,
    expiresAt: query.data.session.expiresAt,
  };
}
